import { useState, useEffect } from 'react';
import { Prompt, PatientInfo, SubtestScores, CompositeScores } from '../types';
import { defaultPrompts } from '../utils/constants';
import { replacePlaceholders as applyPromptPlaceholders } from '../utils/promptEngine';

export const usePromptLibrary = (
  patientInfo: PatientInfo,
  subtestScores: SubtestScores,
  compositeScores: CompositeScores
) => {
  const [promptLibrary, setPromptLibrary] = useState<Prompt[]>(defaultPrompts);
  const [selectedPromptId, setSelectedPromptId] = useState('weronika');
  const [customPrompts, setCustomPrompts] = useState<Prompt[]>([]);
  const [showPromptLibrary, setShowPromptLibrary] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<Prompt | null>(null);
  const [showPromptPreview, setShowPromptPreview] = useState(false);
  const [previewPrompt, setPreviewPrompt] = useState('');
  // System instruction shown next to the preview (v2.1 prompt) — sent separately,
  // never concatenated into the user message.
  const [previewSystemPrompt, setPreviewSystemPrompt] = useState('');

  // Local storage handling
  useEffect(() => {
    const saved = localStorage.getItem('wiscv_custom_prompts');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setCustomPrompts(parsed);
        setPromptLibrary(prev => [...prev.filter(p => !p.isCustom), ...parsed]);
      } catch (error) {
        console.error('Error loading custom prompts:', error);
      }
    }
  }, []);

  const saveCustomPrompts = (prompts: Prompt[]) => {
    setCustomPrompts(prompts);
    localStorage.setItem('wiscv_custom_prompts', JSON.stringify(prompts));
    setPromptLibrary(prev => [...prev.filter(p => !p.isCustom), ...prompts]);
  };

  // Placeholder substitution is delegated to the pure, unit-tested engine
  // in utils/promptEngine.ts.
  const replacePlaceholders = (promptText: string) =>
    applyPromptPlaceholders(promptText, { patientInfo, subtestScores, compositeScores });

  const preparePrompt = (showPreview: boolean = true): string => {
    const requiredComposites = ['vci', 'vsi', 'fri', 'wmi', 'psi', 'fsiq'];
    const missingComposites = requiredComposites.filter(key => !compositeScores[key] || compositeScores[key] === '');

    if (missingComposites.length > 0) {
      alert(`Proszę wypełnić wszystkie wskaźniki złożone: ${missingComposites.map(key => key.toUpperCase()).join(', ')}`);
      return '';
    }

    const selectedPrompt = promptLibrary.find(p => p.id === selectedPromptId);
    if (!selectedPrompt) {
      alert('Nie znaleziono wybranego promptu');
      return '';
    }

    const finalPrompt = replacePlaceholders(selectedPrompt.prompt);
    setPreviewPrompt(finalPrompt);
    setPreviewSystemPrompt(selectedPrompt.systemPrompt ?? '');
    if (showPreview) {
      setShowPromptPreview(true);
    }
    return finalPrompt;
  };

  const createNewPrompt = () => {
    const newPrompt: Prompt = {
      id: 'custom_' + Date.now(),
      name: 'Nowy prompt',
      description: 'Opis nowego promptu',
      isCustom: true,
      prompt: `Jesteś doświadczonym psychologiem diagnostą piszącym interpretację.

DANE DZIECKA:
- Imię: {PATIENT_NAME}
- Wyniki: VCI={VCI_SCORE}, VSI={VSI_SCORE}, FRI={FRI_SCORE}, WMI={WMI_SCORE}, PSI={PSI_SCORE}, FSIQ={FSIQ_SCORE}
- Podtesty: {SUBTEST_SCORES}

INSTRUKCJE STYLISTYCZNE:
[TUTAJ DODAJ SWOJE INSTRUKCJE DOTYCZĄCE STYLU PISANIA]

DŁUGOŚĆ I SZCZEGÓŁOWOŚĆ SEKCJI:
- Ogólna ocena: 2-3 zdania z charakterystyką poziomu i profilu
- Mocne strony: 4-5 zdań z konkretnymi przykładami funkcjonowania
- Obszary trudności: 5-6 zdań z szczegółowymi opisami błędów i mechanizmów
- Funkcjonowanie szkolne: 4-5 zdań o wpływie na naukę
- Zalecenia dla nauczycieli: minimum 8-12 punktów z myślnikami
- Zalecenia dla rodziców: minimum 6-10 punktów z myślnikami

FORMAT ZALECEŃ (BARDZO WAŻNE):
Zalecenia dla nauczycieli i rodziców MUSZĄ być napisane w formie listy z myślnikami, np.:
- pierwsza konkretna strategia lub wskazówka
- druga konkretna strategia lub wskazówka
- trzecia konkretna strategia lub wskazówka

Odpowiedz w formacie Markdown z tymi nagłówkami:

## Ogólna ocena
(formalne rozpoczęcie + charakterystyka poziomu rozwoju i profilu funkcjonowania, 2-3 zdania)

## Mocne strony
(szczegółowy opis mocnych stron funkcjonalnych z konkretnymi przykładami, 4-5 zdań)

## Obszary wymagające wsparcia
(dokładny opis trudności z konkretnymi przykładami błędów i mechanizmów, 5-6 zdań)

## Funkcjonowanie szkolne
(analiza wpływu na funkcjonowanie szkolne z praktycznymi konsekwencjami, 4-5 zdań)

## Zalecenia dla nauczycieli
(LISTA Z MYŚLNIKAMI - minimum 8-12 konkretnych strategii dydaktycznych)

## Zalecenia dla rodziców
(LISTA Z MYŚLNIKAMI - minimum 6-10 praktycznych wskazówek)

NIE używaj formatu JSON - pisz naturalny tekst Markdown.`
    };
    setEditingPrompt(newPrompt);
  };

  const editPrompt = (prompt: Prompt) => {
    setEditingPrompt({ ...prompt });
  };

  const savePrompt = (prompt: Prompt) => {
    if (prompt.isCustom) {
      const updatedCustomPrompts = customPrompts.find(p => p.id === prompt.id)
        ? customPrompts.map(p => p.id === prompt.id ? prompt : p)
        : [...customPrompts, prompt];
      saveCustomPrompts(updatedCustomPrompts);
    } else {
      // Update built-in prompt
      setPromptLibrary(prev => prev.map(p => p.id === prompt.id ? prompt : p));
    }
    setEditingPrompt(null);
  };

  const deletePrompt = (promptId: string) => {
    if (window.confirm('Czy na pewno chcesz usunąć ten prompt?')) {
      const updatedCustomPrompts = customPrompts.filter(p => p.id !== promptId);
      saveCustomPrompts(updatedCustomPrompts);
      if (selectedPromptId === promptId) {
        setSelectedPromptId('weronika');
      }
    }
  };

  const viewPrompt = (prompt: Prompt) => {
    setPreviewPrompt(prompt.prompt);
    setPreviewSystemPrompt(prompt.systemPrompt ?? '');
    setShowPromptPreview(true);
  };

  const copyPromptToClipboard = async () => {
    const textToCopy = previewSystemPrompt
      ? `INSTRUKCJA SYSTEMOWA:\n${previewSystemPrompt}\n\n=====\n\n${previewPrompt}`
      : previewPrompt;
    try {
      await navigator.clipboard.writeText(textToCopy);
      alert('Prompt skopiowany do schowka!');
    } catch (error) {
      console.error('Błąd kopiowania:', error);
      // Fallback method
      const textArea = document.createElement('textarea');
      textArea.value = textToCopy;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('Prompt skopiowany do schowka!');
    }
  };

  /** System instruction of the currently selected prompt (undefined for single-message prompts). */
  const selectedSystemPrompt = promptLibrary.find(p => p.id === selectedPromptId)?.systemPrompt;

  return {
    promptLibrary,
    selectedPromptId,
    selectedSystemPrompt,
    previewSystemPrompt,
    showPromptLibrary,
    editingPrompt,
    showPromptPreview,
    previewPrompt,
    customPrompts,
    setEditingPrompt,
    setShowPromptLibrary,
    setSelectedPromptId,
    setShowPromptPreview,
    preparePrompt,
    createNewPrompt,
    editPrompt,
    savePrompt,
    deletePrompt,
    viewPrompt,
    copyPromptToClipboard
  };
};
